import React, { useState, useRef } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { getClassLevel } from '../../../shared/rules/classIdentity.mjs';
const identifier = () => (typeof window !== 'undefined' ? window.crypto?.randomUUID?.() : null) || `cunning:${Date.now()}:${Math.random()}`;

export function ArcaneRecoveryChoices({ profile, busy, onRecover, name = 'Arcane Recovery' }) {
  const [choices, setChoices] = useState({});
  if (!profile?.available) return profile?.used ? <p>{name} is spent until Long Rest.</p> : null;
  const total = Object.entries(choices).reduce((sum, [level, amount]) => sum + Number(level) * amount, 0);
  return <section aria-label={name} className="mt-3">
    <h5>{name}</h5>
    <p>Choose expended ordinary slots to recover, up to {profile.budget} combined slot levels. Level 6+ and Pact Magic slots cannot be chosen. Optional, once per Long Rest.</p>
    {[1,2,3,4,5].filter(level => profile.spentByLevel[level] > 0).map(level => <label key={level} className="d-flex align-items-center gap-2 mb-2">
      Level {level} · {profile.spentByLevel[level]} expended
      <select aria-label={`Recover level ${level} slots`} disabled={busy} value={choices[level] || 0}
        onChange={event => setChoices(previous => ({ ...previous, [level]: Number(event.target.value) }))}>
        {Array.from({ length: profile.spentByLevel[level] + 1 }, (_, amount) => <option key={amount} value={amount}>{amount}</option>)}
      </select>
    </label>)}
    <p>{total} / {profile.budget} slot levels selected</p>
    <Button disabled={busy || total < 1 || total > profile.budget} onClick={() => onRecover(choices)}>Recover selected slots</Button>
  </section>;
}

export default function SpellRecoveryControls({ form, onCharacterChange }) {
  const [show, setShow] = useState(false), [profile, setProfile] = useState(null);
  const [confirmed, setConfirmed] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const lock = useRef(false), pending = useRef(null);
  const id = form?._id;
  const key = `magical-cunning:${id}`;
  const open = async () => {
    setShow(true); setError(''); setConfirmed(false); setProfile(null);
    try { pending.current = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { pending.current = null; }
    try {
      const response = await apiFetch(`/characters/${id}/magical-cunning`, { cache: 'no-store' });
      const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Unable to load Magical Cunning.');
      setProfile(data);
    } catch (failure) { setError(failure.message); }
  };
  const complete = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const choice = pending.current || { id: identifier(), minuteCompleted: true };
    pending.current = choice;
    try { sessionStorage.setItem(key, JSON.stringify(choice)); } catch { /* In-memory retry remains available. */ }
    try {
      const response = await apiFetch(`/characters/${id}/magical-cunning`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(choice) });
      const data = await response.json();
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) { pending.current = null; try { sessionStorage.removeItem(key); } catch { /* Optional cache. */ } }
        throw new Error(data.message || 'Unable to complete Magical Cunning.');
      }
      pending.current = null; try { sessionStorage.removeItem(key); } catch { /* Optional cache. */ }
      onCharacterChange?.(previous => ({ ...previous, spellcastingState: data.spellcastingState, spellSlotProfile: data.spellSlotProfile }));
      setProfile(data.cunning); setShow(false);
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  if (getClassLevel(form, 'warlock') < 2) return null;
  return <>
    <Button variant="secondary" onClick={open}>Magical Cunning</Button>
    <Modal show={show} onHide={busy ? undefined : () => setShow(false)} centered className="dnd-modal modern-modal">
      <Modal.Header closeButton><Modal.Title>Magical Cunning</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>Complete a one-minute rite outside combat to recover expended Pact Magic slots. Usable once per Long Rest.</p>
        {profile && <p>{profile.recoverable} Pact Magic slot(s) can be recovered, up to {profile.maximum}.</p>}
        {profile?.used && <p>This feature is spent until your DM grants a Long Rest.</p>}
        {error && <Alert variant="danger">{error}</Alert>}
        {pending.current ? <p>A previous result is uncertain. Retry to confirm it without spending another use.</p> : <label><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)}/> The one-minute rite has been completed.</label>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setShow(false)}>Close</Button>
        <Button disabled={busy || (!pending.current && (!confirmed || !profile?.available || profile.recoverable < 1))} onClick={complete}>{pending.current ? 'Retry rite' : 'Recover Pact Magic slots'}</Button></Modal.Footer>
    </Modal>
  </>;
}
